import qrcode from 'qrcode-generator';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBox, Field, Input } from '../components/ui';
import { downloadFile } from '../lib/download';
import { errorMessage } from '../lib/errors';
import { useReady, useSession } from '../lib/session';

interface MfaStatus {
  enabled: boolean;
  enabledAt: string | null;
  recoveryCodesLeft: number;
  required: boolean;
}

function Qr({ text }: { text: string }) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return (
    <img
      className="qr"
      alt="QR code à scanner avec l’application d’authentification"
      src={qr.createDataURL(5, 2)}
    />
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone(): void }) {
  return (
    <div className="card">
      <h2>Vos codes de secours</h2>
      <p>
        Chaque code permet <strong>une</strong> connexion si vous n’avez plus votre téléphone. Conservez-les
        en lieu sûr (gestionnaire de mots de passe, papier) : ils ne seront plus affichés.
      </p>
      <ul className="recovery-codes" aria-label="Codes de secours">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
      <div className="row-inline">
        <button
          className="btn"
          onClick={() =>
            downloadFile(
              `Codes de secours Krotime\n\n${codes.join('\n')}\n`,
              'codes-de-secours-climapp.txt',
              'text/plain;charset=utf-8',
            )
          }
        >
          Télécharger
        </button>
        <button className="btn primary" onClick={onDone}>
          J’ai conservé mes codes
        </button>
      </div>
    </div>
  );
}

/**
 * Double vérification : à la connexion, en plus du mot de passe, un code à 6 chiffres donné par une application
 * d’authentification (Google Authenticator, Microsoft Authenticator, 1Password…). `forced` : l’entreprise l’exige
 * pour les administrateurs, l’application reste bloquée sur cette page tant qu’elle n’est pas activée.
 */
export function SecurityPage({ forced = false }: { forced?: boolean }) {
  const { api, me } = useReady();
  const { refreshMe, logout } = useSession();
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    () =>
      void api.request<MfaStatus>('GET', '/v1/auth/mfa').then(setStatus, (e) => setError(errorMessage(e))),
    [api],
  );
  useEffect(load, [load]);

  const act = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const start = () => act(async () => setSetup(await api.request('POST', '/v1/auth/mfa/setup', {})));
  const enable = () =>
    act(async () => {
      const r = await api.request<{ recoveryCodes: string[] }>('POST', '/v1/auth/mfa/enable', { code });
      setCodes(r.recoveryCodes);
      setSetup(null);
      setCode('');
    });
  const disable = () =>
    act(async () => {
      await api.request('POST', '/v1/auth/mfa/disable', { password, code });
      setCode('');
      setPassword('');
      load();
    });
  const regenerate = () =>
    act(async () => {
      const r = await api.request<{ recoveryCodes: string[] }>('POST', '/v1/auth/mfa/recovery-codes', {
        code,
      });
      setCodes(r.recoveryCodes);
      setCode('');
    });

  const body = (
    <>
      <h1>Sécurité du compte</h1>
      {!forced && <MyPhone />}
      {forced && (
        <p className="warning-box">
          {me.tenant.name} demande aux administrateurs d’activer la <strong>double vérification</strong> avant
          de continuer.
        </p>
      )}
      <ErrorBox message={error} />
      {codes ? (
        <RecoveryCodes
          codes={codes}
          onDone={() => {
            setCodes(null);
            load();
            if (forced) void refreshMe();
          }}
        />
      ) : status && !status.enabled ? (
        <div className="card form">
          <h2>Double vérification : désactivée</h2>
          <p>
            À chaque connexion, en plus du mot de passe, un code à 6 chiffres sera demandé. Il est donné par
            une application d’authentification installée sur votre téléphone (Google Authenticator, Microsoft
            Authenticator, 1Password…).
          </p>
          {!setup ? (
            <button className="btn primary" onClick={() => void start()}>
              Activer la double vérification
            </button>
          ) : (
            <>
              <ol>
                <li>
                  Ouvrez l’application d’authentification et ajoutez un compte (« + » ou « Scanner un QR code
                  »).
                </li>
                <li>Scannez ce QR code :</li>
              </ol>
              <Qr text={setup.otpauthUrl} />
              <p className="muted">
                Impossible de scanner ? Saisissez cette clé :{' '}
                <code className="secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              </p>
              <Field label="3. Code affiché par l’application">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="Code de vérification"
                />
              </Field>
              <button className="btn primary" disabled={code.trim().length < 6} onClick={() => void enable()}>
                Vérifier et activer
              </button>
            </>
          )}
        </div>
      ) : status ? (
        <div className="card form">
          <h2>Double vérification : activée</h2>
          <p className="muted">
            Depuis le {new Date(status.enabledAt!).toLocaleDateString('fr-FR')} · {status.recoveryCodesLeft}{' '}
            code(s) de secours restant(s).
          </p>
          <Field label="Code de l’application (ou code de secours)">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Code de vérification"
            />
          </Field>
          <div className="row-inline">
            <button className="btn" disabled={code.trim().length < 6} onClick={() => void regenerate()}>
              Nouveaux codes de secours
            </button>
          </div>
          {status.required ? (
            <p className="muted">
              La double vérification est obligatoire pour les administrateurs de l’entreprise.
            </p>
          ) : (
            <>
              <Field label="Mot de passe (pour désactiver)">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </Field>
              <button
                className="btn danger"
                disabled={!password || code.trim().length < 6}
                onClick={() => void disable()}
              >
                Désactiver la double vérification
              </button>
            </>
          )}
        </div>
      ) : null}
      <p className="muted">
        Téléphone perdu ? Utilisez un code de secours, ou demandez à un administrateur de réinitialiser votre
        double vérification (Administration → Équipe).
      </p>
    </>
  );

  if (forced)
    return (
      <div className="login">
        <div className="login-wide">
          {body}
          <button className="link" onClick={() => void logout()}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  return (
    <div>
      {body}
      <p>
        <Link to="/">Retour</Link>
      </p>
    </div>
  );
}

/** Mon numéro de portable : pour recevoir les SMS « consultez votre application » (interventions, prospects). */
function MyPhone() {
  const { api, me } = useReady();
  const { refreshMe } = useSession();
  const [phone, setPhone] = useState(me.user.phone ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="card form">
      <h2>Mes coordonnées</h2>
      <Field label="Mon téléphone portable (SMS de l’entreprise)">
        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0690 12 34 56" />
      </Field>
      <ErrorBox message={error} />
      {msg && <p className="muted">{msg}</p>}
      <div className="actions">
        <button
          className="btn primary"
          onClick={() => {
            setError(null);
            setMsg(null);
            api
              .request('PATCH', '/v1/auth/me', { phone: phone.trim() || null })
              .then(() => (setMsg('Numéro enregistré.'), void refreshMe()), (e) => setError(errorMessage(e)));
          }}
        >
          Enregistrer mon numéro
        </button>
      </div>
    </div>
  );
}
